import {LabelItem} from "../../model/items/labelItem";
import Image from "../shared/components/Image/Image";
import {TextboxItem} from "../../model/items/textbox";

export type TMaxDiffRow = {
    label: LabelItem,
    image?: Image,
    ratings: TextboxItem[],
};

export default class MaxDiffState {

    public static readonly RATING_NOT_SHOWN = 0;
    public static readonly RATING_WIN = 1;
    public static readonly RATING_SHOWN = 2;
    public static readonly RATING_LOSE = 3;

    private _round: number = 0;

    constructor(
        public readonly rows: TMaxDiffRow[],
        public readonly options: {
            finalRound: boolean,
            itemsPerRound: number
        }
    ) {
        this.initRound();
    }

    private initRound() {
        const roundCount = this.getRoundCount();
        this._round = 0;
        while(this.round < roundCount-1 && this.canNext()) {
            this._round++;
        }
    }

    public get round(): number {
        return this._round;
    }

    public prevRound(): void {
        this._round--;
    }

    public nextRound(): void {
        this._round++;
    }

    public isLastRound(): boolean {
        return this.getRoundCount()-1 === this._round;
    }

    public getCurrentRows(): TMaxDiffRow[] {
        if (this.isFinalRound()) {
            return this.rows
                .filter(row => row.ratings
                    .slice(0, -1)
                    .some(rating => rating.getVariable() === MaxDiffState.RATING_WIN)
                );
        }
        const start = this.round*this.options.itemsPerRound;
        return this.rows.slice(start, start+this.options.itemsPerRound);
    }

    public getCurrentRowsWithRating(rating: number): TMaxDiffRow[] {
        const rows = this.getCurrentRows();
        const ratings = this.getCurrentRatings();
        return rows.filter((row,i) => ratings[i].getVariable() == rating);
    }

    private getCurrentRatings(): TextboxItem[] {
        return this.getCurrentRows().map(row => row.ratings[this.round]);
    }

    /**
     * Set win or lose to some of currently visible row
     * @param changedRow
     * @param value
     */
    public setValue(changedRow: TMaxDiffRow, value: number): void {

        const currentRatings = this.getCurrentRatings();

        this.rows.forEach(row => {
            const rating = row.ratings[this.round];
            if (currentRatings.includes(rating)) {
                // Currently shown rows
                const oldValue = rating.getVariable();
                if (oldValue === MaxDiffState.RATING_WIN || oldValue === MaxDiffState.RATING_LOSE) {
                    if (oldValue === value) {
                        // Set clicked value to new position
                        rating.set(row === changedRow ? value : MaxDiffState.RATING_SHOWN);
                    } else {
                        if (row === changedRow) {
                            rating.set(value);
                        }
                    }
                } else {
                    // Other shown rows mark as shown
                    rating.set(row === changedRow ? value : MaxDiffState.RATING_SHOWN)
                }
            } else {
                // Currently shown rows
                rating.set(MaxDiffState.RATING_NOT_SHOWN);
            }
        });

        if (this.options.finalRound && !this.isFinalRound()) {
            this.rows.map(row => row.ratings[row.ratings.length-1])
                .forEach(ci => ci.reset());
        }
    }

    public getRoundCount(): number {
        return this.rows[0].ratings.length;
    }

    public isFinalRound(): boolean {
        return this.options.finalRound && this.getRoundCount()-1 === this.round;
    }

    public canNext(): boolean {
        const currentValues = this.getCurrentRatings().map(tb => tb.getVariable());
        return currentValues.filter(v => v === MaxDiffState.RATING_WIN).length > 0
            && (this.isFinalRound() || currentValues.filter(v => v === MaxDiffState.RATING_LOSE).length > 0);
    }

}
